import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import TextInput from "../components/TextInput";
import GenderSelect from "../components/GenderSelect";
import DOBSelect from "../components/DOBSelect";

import { useAuth } from "../utils/auth";
import { sdkWrapperURL } from "../utils/api-url-list";

import { ReactComponent as BackArrow } from "../assets/icons/BackArrow.svg";
import { ReactComponent as Loader } from "../assets/icons/Loader.svg";

const MyAccount = () => {
  const { getToken, userData } = useAuth();

  const fnameRef = useRef(null);
  const lnameRef = useRef(null);
  const emailRef = useRef(null);
  const heightRef = useRef(null);
  const weightRef = useRef(null);
  const pincodeRef = useRef(null);

  const [fname, setFName] = useState(userData.fname);
  const [lname, setLName] = useState(userData.lname);
  const [email, setEmail] = useState(userData.email);
  const [gender, setGender] = useState(userData.gender);
  const [dob, setDOB] = useState(userData.dob);
  const [height, setHeight] = useState(userData.height?.toString?.());
  const [weight, setWeight] = useState(userData.weight?.toString?.());
  const [pincode, setPincode] = useState(userData.pincode);

  const [fnameError, setFNameError] = useState("");
  const [lnameError, setLNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [genderError, setGenderError] = useState("");
  const [dobError, setDOBError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [weightError, setWeightError] = useState("");
  const [pincodeError, setPincodeError] = useState("");

  const heightFtIn = useMemo(() => {
    const parsedHeight = parseFloat(height);
    if (isNaN(parsedHeight)) return "- ft. -- in.";
    const totalInches = parsedHeight / 2.54;
    const feet = Math.floor(totalInches / 12);
    const remainingInches = Math.round(totalInches % 12);
    return `${feet} ft. ${remainingInches} in.`;
  }, [height]);
  const weightLbs = useMemo(() => {
    const parsedWeight = parseFloat(weight);
    if (isNaN(parsedWeight)) return "--- lbs";
    return `${Math.round(parsedWeight * 2.20462)} lbs`;
  }, [weight]);

  const [isLoading, setLoading] = useState(false);
  const [hasSaved, setSaved] = useState(false);

  const updateProfile = async () => {
    try {
      setLoading(true);
      setFNameError("");
      setLNameError("");
      setEmailError("");
      setGenderError("");
      setDOBError("");
      setHeightError("");
      setWeightError("");
      setPincodeError("");
      let error = "";
      const trim_pincode = pincode.trim();
      if (!/^\d{6}$/.test(trim_pincode)) {
        setPincodeError("Please enter valid 6-digit Pin Code.");
        error = "pincode";
      }
      setPincode(trim_pincode);
      const trim_weight = weight.trim();
      if (
        !/^\d{1,3}(\.\d{1,2})?$/.test(trim_weight) ||
        trim_weight < 20 ||
        trim_weight > 220
      ) {
        setWeightError("Please enter valid Weight.");
        error = "weight";
      }
      setWeight(trim_weight);
      const trim_height = height.trim();
      if (
        !/^\d{1,3}(\.\d{1,2})?$/.test(trim_height) ||
        trim_height < 30 ||
        trim_height > 250
      ) {
        setHeightError("Please enter valid Height.");
        error = "height";
      }
      setHeight(trim_height);
      const bmi = trim_weight / (trim_height / 100) ** 2;
      if (bmi < 10 || bmi > 100) {
        setHeightError("Please enter valid Height.");
        setWeightError("Please enter valid Weight.");
        error = "bmi";
      }
      if (dob.length === 0) {
        setDOBError("Please select your Date of Birth.");
        error = "dob";
      }
      if (gender.length === 0) {
        setGenderError("Please select your Gender.");
        error = "gender";
      }
      const trim_email = email.toLowerCase().trim();
      if (
        trim_email.length > 0 &&
        !/^[a-z0-9]+([._-]*[a-z0-9])+[@](\w+[.])+\w+$/.test(trim_email)
      ) {
        setEmailError(
          "Invalid email address. Please enter a correct email address and try again."
        );
        error = "email";
      }
      setEmail(trim_email);
      const trim_lname = lname.trim();
      if (!/^[a-zA-Z][\w.'’-]*( [\w.'’-]+)?$/.test(trim_lname)) {
        if (trim_lname.length > 0) {
          setLNameError("No special characters are allowed.");
        } else {
          setLName("");
          setLNameError("Please enter your Last Name.");
        }
        error = "lname";
      }
      setLName(trim_lname);
      const trim_fname = fname.trim();
      if (!/^[a-zA-Z][\w.'’-]*( [\w.'’-]+)?$/.test(trim_fname)) {
        if (trim_fname.length > 0) {
          setFNameError("No special characters are allowed.");
        } else {
          setFName("");
          setFNameError("Please enter your First Name.");
        }
        error = "fname";
      }
      setFName(trim_fname);
      if (error.length > 0) {
        switch (error) {
          case "fname":
            fnameRef.current?.focus?.();
            break;
          case "lname":
            lnameRef.current?.focus?.();
            break;
          case "email":
            emailRef.current?.focus?.();
            break;
          case "height":
            heightRef.current?.focus?.();
            break;
          case "weight":
            weightRef.current?.focus?.();
            break;
          case "pincode":
            pincodeRef.current?.focus?.();
            break;
          default:
            break;
        }
      } else {
        const token = await getToken();
        const saveResp = await fetch(sdkWrapperURL("/users/profile/update"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({
            first_name: trim_fname,
            last_name: trim_lname,
            email: trim_email,
            gender: gender,
            dob: dob,
            height: trim_height,
            weight: trim_weight,
            pincode: trim_pincode,
            is_terms_accepted: true,
            latitude: userData.lat,
            longitude: userData.long,
          }),
        });
        const saveRespJSON = await saveResp.json();
        if (saveRespJSON?.statusCode?.toString().startsWith("2")) {
          setSaved(true);
          setTimeout(() => setSaved(false), 2500);
          setTimeout(() => window.location.reload(), 3000);
        } else throw new Error(saveRespJSON?.message ?? "Error in Saving");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="px-6 py-2">
      <div className="flex items-center text-primary">
        <Link className="flex-shrink-0 p-1 pr-2.5" to="/">
          <BackArrow className="h-2.5 w-2.5" />
        </Link>
        <h3 className="text-sm font-medium">My Account</h3>
      </div>

      <form
        className="my-6"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!isLoading) await updateProfile();
        }}
        noValidate
      >
        <div className="space-y-4">
          <TextInput
            inputRef={fnameRef}
            type="text"
            id="fname"
            label="First Name"
            placeholder="Enter your First Name"
            autoComplete="given-name"
            required
            maxLength={25}
            onChangeText={setFName}
            value={fname}
            error={fnameError}
          />
          <TextInput
            inputRef={lnameRef}
            type="text"
            id="lname"
            label="Last Name"
            placeholder="Enter your Last Name"
            autoComplete="family-name"
            required
            maxLength={25}
            onChangeText={setLName}
            value={lname}
            error={lnameError}
          />
          <TextInput
            inputRef={emailRef}
            type="email"
            id="email"
            label="Email"
            placeholder="Enter your Email"
            autoComplete="email"
            inputMode="email"
            onChangeText={setEmail}
            value={email}
            error={emailError}
          />
          <GenderSelect
            label="Gender"
            required
            onSelect={(selectedGender) => {
              setGenderError("");
              setGender(selectedGender);
            }}
            value={gender}
            error={genderError}
          />
          <DOBSelect
            id="dob"
            label="Date of Birth"
            placeholder="Select your Date of Birth"
            required
            onSelect={setDOB}
            value={dob}
            error={dobError}
          />
          <div>
            <label htmlFor="height" className="text-primary/60 text-xs">
              Height (cms)
              <span className="text-error">&nbsp;*</span>
            </label>
            <div className={`relative mt-2 text-primary text-xs`}>
              <input
                type="text"
                ref={heightRef}
                id="height"
                name="height"
                className={`block w-full pl-4 py-2 pr-28 rounded-lg outline-none border ${
                  heightError.length > 0
                    ? "border-error bg-error/5"
                    : "border-lightgray/20"
                } focus:border-secondary`}
                placeholder="Enter your Height in cms"
                inputMode="decimal"
                required
                onChange={(e) => setHeight(e.target.value)}
                value={height?.toString?.() ?? ""}
              />
              <p className="absolute right-0 inset-y-0 w-24 flex items-center justify-center px-4 py-2 rounded-r-lg bg-lightgray/25">
                {heightFtIn}
              </p>
            </div>
            {heightError.length > 0 && (
              <p className="mt-1.5 text-xxs text-error">{heightError}</p>
            )}
          </div>
          <div>
            <label htmlFor="weight" className="text-primary/60 text-xs">
              Weight (kgs)
              <span className="text-error">&nbsp;*</span>
            </label>
            <div className={`relative mt-2 text-primary text-xs`}>
              <input
                type="text"
                ref={weightRef}
                id="weight"
                name="weight"
                className={`block w-full pl-4 py-2 pr-28 rounded-lg outline-none border ${
                  weightError.length > 0
                    ? "border-error bg-error/5"
                    : "border-lightgray/20"
                } focus:border-secondary`}
                placeholder="Enter your Weight in kgs"
                inputMode="decimal"
                required
                onChange={(e) => setWeight(e.target.value)}
                value={weight?.toString?.() ?? ""}
              />
              <p className="absolute right-0 inset-y-0 w-24 flex items-center justify-center px-4 py-2 rounded-r-lg bg-lightgray/25">
                {weightLbs}
              </p>
            </div>
            {weightError.length > 0 && (
              <p className="mt-1.5 text-xxs text-error">{weightError}</p>
            )}
          </div>
          <TextInput
            inputRef={pincodeRef}
            type="text"
            id="pincode"
            label="Pincode"
            placeholder="Enter your Home Pincode"
            autoComplete="postal-code"
            inputMode="numeric"
            required
            maxLength={6}
            onChangeText={setPincode}
            value={pincode}
            error={pincodeError}
          />
        </div>
        <button
          className="mt-8 mx-auto w-40 flex items-center justify-center space-x-2 rounded-full px-4 py-2 bg-primary disabled:bg-darkgray text-white"
          type="submit"
          disabled={isLoading}
        >
          <span className="text-xs font-medium">Update</span>
          {isLoading && <Loader className="flex-shrink-0 h-4 w-4" />}
        </button>
      </form>
      {hasSaved && (
        <p className="mb-3 text-center text-xxs text-secondary">
          Profile Updated Successfully ✅
        </p>
      )}
    </section>
  );
};

export default MyAccount;
